import { IconUpload } from "@tabler/icons-react";
import React, { ChangeEvent, Dispatch, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@components/ui/button";
import { ButtonWithTooltipProps } from "@components/ui/custom/button-with-tooltip";
import { Spinner } from "@components/ui/spinner";
import { cn } from "@lib/utils";

export const UploadButton = ({
  loading,
  setLoading,
  onFileSelect,
  accept,
  className,
  ...props
}: ButtonWithTooltipProps & {
  onFileSelect: (form: FormData) => Promise<void>;
  loading?: boolean;
  setLoading?: Dispatch<boolean>;
  accept?: string;
}) => {
  const tCommon = useTranslations("common");
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.currentTarget.files?.[0];

      if (selectedFile) {
        // Allow selecting the same file again after either a failed or successful upload.
        event.currentTarget.value = "";
        const formData = new FormData();
        setLoading?.(true);
        formData.append("file", selectedFile);
        onFileSelect(formData).finally(() => {
          setTimeout(() => setLoading?.(false), 500);
        });
      }
    },
    [onFileSelect, setLoading],
  );

  return (
    <>
      <Button
        {...props}
        disabled={loading ? loading : props.disabled}
        className={cn("w-full cursor-pointer md:w-fit", className)}
        onClick={() => inputRef.current?.click()}
      >
        {!loading && <IconUpload className="mr-2 h-4 w-4" />}
        {loading && <Spinner />}
        {!props.size?.includes("icon") && tCommon("words.verbs.selectFile")}
      </Button>
      <input
        ref={inputRef}
        id="photo-upload"
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={loading ? loading : props.disabled}
        className="hidden"
      />
    </>
  );
};
